import React, { useState } from "react";
import { Form, Input, Button } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { login } from "./authService";
import "./auth.css";

const Login = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const data = await login(values.email, values.password);
      const role = data?.roles?.[0];

      localStorage.setItem("userId", data.id);
      localStorage.setItem("personalId", data.personalId);
      localStorage.setItem("token", data.token);
      localStorage.setItem("role", role);
      localStorage.setItem("presenceHubStarted", "true");
      localStorage.setItem("platformUserType", data.platformUserType);

      toast.success(`Welcome ${data?.fullName || "back"}! Login successful.`);

      const rolePaths = {
        SuperAdmin: "/proAdmin-dashboard",
        Admin: "/admin-dashboard",
        Mentor: "/mentor-dashboard",
        Learner: "/learner-dashboard",
        Manager: "/manager-dashboard",
        SelfLearner: "/selfLearner-dashboard",
        Expert: "/expert-dashboard",
      };

      if (data.platformUserType === 0) {
        // Organization users (Admin, Mentor, Learner, Manager)
        navigate(rolePaths[role] || "/");
      } else if (data.platformUserType === 1) {
        // Individual users (SelfLearner, Expert, or CORIQPRO_Admin)
        if (role === "CORIQPRO_Admin") {
          navigate(rolePaths.SuperAdmin);
        } else if (role === "SelfLearner") {
          navigate(rolePaths.SelfLearner);
        } else if (role === "Expert") {
          navigate(rolePaths.Expert);
        } else {
          navigate("/");
        }
      }

      window.location.reload();
    } catch (error) {
      console.error("Login failed:", error);

      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          toast.error(errorData);
        } else if (errorData.message) {
          toast.error(errorData.message);
        } else if (errorData.errors) {
          const errorMessages = Object.values(errorData.errors).flat();
          errorMessages.forEach((msg) => toast.error(msg));
        } else if (errorData.title) {
          toast.error(errorData.title);
        } else {
          toast.error("Invalid email or password");
        }
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ciq-auth">
      {/* Left: brand showcase */}
      <aside className="ciq-auth__brand">
        <div className="ciq-auth__brand-bg" />
        <div className="ciq-auth__brand-inner">
          <a className="ciq-brand" href="https://coreiqpro.com">
            <img src={`${process.env.PUBLIC_URL}/logo-icon.png`} alt="CoreIQPro" />
            <span className="ciq-brand__txt">
              Core<span className="o">IQ</span>Pro
            </span>
          </a>
          <h1 className="ciq-auth__headline">Turn training into measurable performance.</h1>
          <p className="ciq-auth__sub">
            Data-driven learning for organizations and individuals, with mentorship,
            assessments, and real progress you can see.
          </p>
          <ul className="ciq-auth__points">
            <li>
              <i className="fas fa-check" /> Role-based learning for whole teams
            </li>
            <li>
              <i className="fas fa-check" /> Self-paced paths for individuals
            </li>
            <li>
              <i className="fas fa-check" /> Skills, assessments and certificates
            </li>
          </ul>
        </div>
      </aside>

      {/* Right: login form */}
      <main className="ciq-auth__panel">
        <motion.div
          className="ciq-auth__card"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="ciq-auth__card-head">
            <div>
              <h2>Welcome back</h2>
              <p className="ciq-auth__muted">Log in to continue to CoreIQPro</p>
            </div>
          </div>

          <Form name="login-form" layout="vertical" onFinish={handleLogin} requiredMark={false}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter your email!" },
                { type: "email", message: "Enter a valid email!" },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="you@company.com"
                autoComplete="email"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: "Please enter your password!" },
                { min: 8, message: "Password must be at least 8 characters!" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Enter your password"
                autoComplete="current-password"
                size="large"
              />
            </Form.Item>

            <div className="ciq-auth__forgot">
              <Button type="link" onClick={() => navigate("/forgot-password")}>
                Forgot password?
              </Button>
            </div>

            <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
              <motion.div
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 20 }}
              >
                <Button type="primary" htmlType="submit" block loading={loading} disabled={loading}>
                  {loading ? "Logging in..." : "Log in"}
                </Button>
              </motion.div>
            </Form.Item>
          </Form>

          <div className="ciq-auth__foot">
            <span className="ciq-auth__muted">New to CoreIQPro?</span>
            <Button type="link" style={{ padding: "0 6px" }} onClick={() => navigate("/startup")}>
              Create an account
            </Button>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Login;
